import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { AppHeader } from '@/shared/components/layout/AppHeader'
import { CompetitionPicker } from '@/features/admin/components/CompetitionPicker'
import { PersonsFilters } from '@/features/admin/components/PersonsFilters'
import { PersonsTable } from '@/features/admin/components/PersonsTable'
import type { PersonsFilter, PersonsPositionFilter } from '@/features/admin/queries'
import styles from './persons.module.css'

type AdminPersonsSearch = {
  competitionId?: string
  teamId?:        string
  position?:      PersonsPositionFilter
}

const POSITIONS = ['GK', 'DEF', 'MID', 'FWD', 'NONE'] as const

export const Route = createFileRoute('/_authenticated/admin/persons')({
  validateSearch: (search: Record<string, unknown>): AdminPersonsSearch => {
    const result: AdminPersonsSearch = {}
    if (typeof search.competitionId === 'string' && search.competitionId) {
      result.competitionId = search.competitionId
    }
    if (typeof search.teamId === 'string' && search.teamId) {
      result.teamId = search.teamId
    }
    if (typeof search.position === 'string' && (POSITIONS as readonly string[]).includes(search.position)) {
      result.position = search.position as PersonsPositionFilter
    }
    return result
  },
  component: AdminPersonsPage,
})

function AdminPersonsPage() {
  const { competitionId, teamId, position } = Route.useSearch()
  const navigate = useNavigate()

  const filter: PersonsFilter = {}
  if (teamId)   filter.teamId   = teamId
  if (position) filter.position = position

  function setCompetition(id: string) {
    // Changing competition wipes team/position so the filter never points at a stale teamId.
    void navigate({
      to:      '/admin/persons',
      search:  { competitionId: id },
      replace: true,
    })
  }

  function setFilter(next: PersonsFilter) {
    void navigate({
      to:      '/admin/persons',
      search:  {
        ...(competitionId ? { competitionId } : {}),
        ...(next.teamId   ? { teamId:   next.teamId }   : {}),
        ...(next.position ? { position: next.position } : {}),
      },
      replace: true,
    })
  }

  return (
    <>
      <AppHeader title="Admin · Pessoas" />
      <div className={styles.page}>
        <Link
          to="/admin"
          search={competitionId ? { competitionId } : {}}
          className={styles.backLink}
        >
          <ChevronLeft size={14} />
          Voltar
        </Link>

        <section className={styles.section}>
          <CompetitionPicker value={competitionId ?? null} onChange={setCompetition} />
        </section>

        <section className={styles.section}>
          <PersonsFilters
            competitionId={competitionId ?? null}
            filter={filter}
            onChange={setFilter}
          />
        </section>

        <section className={styles.section}>
          <PersonsTable competitionId={competitionId ?? null} filter={filter} />
        </section>
      </div>
    </>
  )
}
